/**
 * Draining the outbox — the part that decides what actually happened.
 *
 * Split out of the shop-chat page for two reasons. It used to live in a
 * `useEffect` inside `ChannelThread`, which meant the queue only ever drained
 * while a tech happened to be looking at Shop Chat; walk to Diagnose after
 * reconnecting and the message sat in IndexedDB for the rest of the shift while
 * the banner said "Catching up". And because it lived in an effect, nothing
 * could test it — IndexedDB doesn't exist under happy-dom.
 *
 * Everything here takes its storage and its transport as arguments, so the
 * decisions (whose message this is, what sent, what the server refused, what is
 * merely waiting on a network) are verifiable without a browser.
 */

import type { OutboxItem } from './outbox';

/**
 * How many times the SERVER can refuse one message before we stop offering it.
 *
 * This counts rejections only — never a failure to reach the shop. That
 * distinction is the whole design: on a tablet associated to an AP with a dead
 * uplink, a wall-clock retry budget would burn out in about two minutes and
 * declare a perfectly good message unsendable, which is the pilot's normal
 * Tuesday. A message the server actively refuses is a different animal: it is
 * malformed, or its channel is gone, and offering it forever is what let the
 * banner claim it was "catching up" on something that could never land.
 */
export const MAX_SEND_ATTEMPTS = 5;

/** Exactly the input `messages.send` takes — the queued copy must not be lossier. */
export interface QueuedShopChatPayload {
  channelId: string;
  content: string;
  photoIds?: string[];
  taggedUserIds?: string[];
}

export interface DrainDeps {
  /** Only this user's messages are sent. The tablet changes hands all shift. */
  userId: string;
  list: () => Promise<OutboxItem[]>;
  send: (payload: QueuedShopChatPayload) => Promise<unknown>;
  remove: (id: string) => Promise<void>;
  /** Records a rejection against the item's budget. */
  bump: (id: string) => Promise<void>;
  /** Records a send that landed but whose row we then failed to delete. */
  markDelivered: (id: string) => Promise<void>;
  /** Checked between items so an unmount or a newer drain can cut this one short. */
  shouldStop?: () => boolean;
}

export interface DrainResult {
  sent: number;
  /** The server refused it. Spends a retry attempt. */
  rejected: number;
  /** Couldn't reach the shop. Costs nothing; we try again later. */
  unreachable: number;
  /** Refused MAX_SEND_ATTEMPTS times. Not offered again. */
  stuck: number;
  /** Channels that received something, so the caller can refetch just those. */
  channelIds: string[];
}

const EMPTY: DrainResult = { sent: 0, rejected: 0, unreachable: 0, stuck: 0, channelIds: [] };

/**
 * Did the server refuse this, or could we simply not reach it?
 *
 * Defaults to unreachable. Getting it wrong in that direction costs a retry
 * that was going to happen anyway; getting it wrong the other way throws away
 * a technician's words because the wifi dipped.
 */
export function isRejection(err: unknown): boolean {
  const status = (err as { data?: { httpStatus?: number } })?.data?.httpStatus;
  return typeof status === 'number' && status >= 400 && status < 500;
}

/** A message this device is holding for somebody else — or from before we recorded who. */
export function belongsTo(item: OutboxItem, userId: string): boolean {
  return item.userId === userId;
}

export async function drainOutbox(deps: DrainDeps): Promise<DrainResult> {
  const queue = await deps.list().catch(() => [] as OutboxItem[]);
  if (queue.length === 0) return EMPTY;

  let sent = 0;
  let rejected = 0;
  let unreachable = 0;
  let stuck = 0;
  const channelIds = new Set<string>();

  for (const item of queue) {
    if (deps.shouldStop?.()) break;
    // The union carries a 'diagnose' arm that nothing enqueues today. Skip
    // rather than assume, so wiring that surface later can't silently post a
    // diagnose turn into a chat channel.
    if (item.channel !== 'shopchat') continue;
    // Not ours to send. It waits for whoever wrote it to sign back in.
    if (!belongsTo(item, deps.userId)) continue;

    // Already landed; we just couldn't cross it off last time. Never re-send.
    if (item.deliveredAt) {
      await deps.remove(item.id).catch(() => undefined);
      continue;
    }

    if (item.attempts >= MAX_SEND_ATTEMPTS) {
      stuck += 1;
      continue;
    }

    const payload = item.payload as QueuedShopChatPayload;
    try {
      await deps.send(payload);
    } catch (err) {
      if (isRejection(err)) {
        rejected += 1;
        await deps.bump(item.id).catch(() => undefined);
      } else {
        unreachable += 1;
      }
      continue;
    }

    sent += 1;
    if (payload?.channelId) channelIds.add(payload.channelId);

    // Deliberately outside the send try. A delete that fails is not a send that
    // failed — reading it as one is how the same message gets posted twice. It
    // did send; we just couldn't cross it off, so mark it delivered and the
    // next pass will only retry the delete.
    await deps.remove(item.id).catch(async () => {
      await deps.markDelivered(item.id).catch(() => undefined);
    });
  }

  return { sent, rejected, unreachable, stuck, channelIds: [...channelIds] };
}
